import { Link } from "react-router-dom";
import { ArrowUpRight, Plane, Shield, Sparkles, Globe2 } from "lucide-react";
import PublicNav from "@/components/PublicNav";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAF7F0] text-[#1A1D24]">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden as-grain">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/12802014/pexels-photo-12802014.jpeg?auto=compress&cs=tinysrgb&w=1800"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1D24]/70 via-[#1A1D24]/50 to-[#FAF7F0]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1D24]/80 via-[#1A1D24]/20 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-36 md:pb-44 grid grid-cols-1 md:grid-cols-12 gap-8 text-white">
          <div className="md:col-span-8 as-fade-up">
            <div className="as-overline mb-6 !text-white/70" data-testid="hero-overline">— Private Aviation · Bespoke Concierge · Worldwide</div>
            <h1 className="as-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] font-light text-white" data-testid="hero-title">
              The world moves fast.<br/>
              <span className="italic text-[#E5C158]">Your world</span> moves faster.
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed font-light" data-testid="hero-sub">
              A single, invisible platform that orchestrates private jet trips, permits, ground handling, and luxury concierge — for principals, family offices, and Fortune-500 travel desks.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/charter" className="as-btn-gold px-6 py-3.5 rounded-sm as-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="hero-charter-cta">
                Charter a Jet <ArrowUpRight size={14}/>
              </Link>
              <Link to="/login" className="px-6 py-3.5 rounded-sm as-mono text-xs uppercase tracking-[0.2em] border border-white/40 text-white hover:border-white hover:bg-white/10 transition-colors" data-testid="hero-signin">
                Client Sign-in
              </Link>
            </div>
          </div>
          <div className="hidden md:flex md:col-span-4 items-end justify-end">
            <div className="text-right as-fade-up as-delay-2">
              <div className="as-mono text-[10px] uppercase tracking-widest text-white/60">Operational since</div>
              <div className="as-serif text-6xl font-light mt-2 text-white">2019</div>
              <div className="h-px bg-white/20 my-6"/>
              <div className="grid grid-cols-2 gap-6 text-right">
                <div>
                  <div className="as-serif text-3xl text-[#E5C158]">142</div>
                  <div className="as-mono text-[10px] uppercase tracking-widest text-white/60 mt-1">countries<br/>served</div>
                </div>
                <div>
                  <div className="as-serif text-3xl text-[#E5C158]">3.4k</div>
                  <div className="as-mono text-[10px] uppercase tracking-widest text-white/60 mt-1">missions<br/>completed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS STRIP */}
      <section className="border-y border-[#E5DFD2] bg-[#FAF7F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            {k: "24 / 7", v: "Dispatch"},
            {k: "≤ 4 hrs", v: "Trip Confirmation"},
            {k: "1 : 1", v: "Dedicated Ops Owner"},
            {k: "AES-256", v: "Encrypted Itineraries"},
          ].map((m, i) => (
            <div key={i} className="flex flex-col gap-1" data-testid={`metric-${i}`}>
              <div className="as-serif text-3xl md:text-4xl text-[#1A1D24]">{m.k}</div>
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES — asymmetric bento */}
      <section id="services" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-5">
            <div className="as-overline mb-4">01 — Capabilities</div>
            <h2 className="as-serif text-4xl md:text-6xl leading-[1] font-light">One platform. <br/><span className="italic text-[#A47E2A]">Every</span> touchpoint.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-[#756E64] font-light">From landing permits at short notice to a Feadship in Antibes — we operate the entire mission, and your dashboard shows every move in real-time.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 as-card p-8 md:p-10 relative overflow-hidden group" data-testid="service-aviation">
            <img src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?auto=format&fit=crop&w=1400&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity duration-500"/>
            <div className="relative">
              <Plane size={22} className="as-gold" strokeWidth={1.3}/>
              <div className="as-serif text-3xl md:text-4xl mt-6 font-light">Aviation Operations</div>
              <p className="text-[#756E64] font-light mt-3 max-w-md">Flight planning, landing & overflight permits, FBO coordination, fuel, customs, crew logistics — orchestrated on a single trip timeline.</p>
            </div>
          </div>
          <div className="md:col-span-5 as-card p-8 md:p-10" data-testid="service-concierge">
            <Sparkles size={22} className="as-gold" strokeWidth={1.3}/>
            <div className="as-serif text-3xl mt-6 font-light">Luxury Concierge</div>
            <p className="text-[#756E64] font-light mt-3">Chauffeur, hotels, yachts, helicopters, executive security, restaurant tables — booked and confirmed inside the same trip.</p>
          </div>
          <div className="md:col-span-4 as-card p-8 md:p-10" data-testid="service-security">
            <Shield size={22} className="as-gold" strokeWidth={1.3}/>
            <div className="as-serif text-3xl mt-6 font-light">Discretion</div>
            <p className="text-[#756E64] font-light mt-3">Compartmentalised access, encrypted itineraries, and vetted operators. Your travel is invisible to everyone but you.</p>
          </div>
          <div className="md:col-span-8 as-card p-8 md:p-10 relative overflow-hidden" data-testid="service-network">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <Globe2 size={22} className="as-gold" strokeWidth={1.3}/>
                <div className="as-serif text-3xl md:text-4xl mt-6 font-light">Global Network</div>
                <p className="text-[#756E64] font-light mt-3">Vetted FBOs in 142 countries. Preferred rates with Signature, Universal, Peninsula, Aman, Blacklane and more.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                {["Signature", "Universal", "Peninsula", "Aman", "Blacklane", "Control Risks"].map(x => (
                  <div key={x} className="border border-[#E5DFD2] py-3 as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{x}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="border-t border-[#E5DFD2] bg-[#FAF7F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <img src="https://images.pexels.com/photos/14914172/pexels-photo-14914172.jpeg?auto=compress&cs=tinysrgb&w=1400" alt="" className="w-full h-[520px] object-cover"/>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="as-overline mb-6">02 — Standard of care</div>
            <h2 className="as-serif text-4xl md:text-5xl font-light leading-[1.05]">Curated for principals who <span className="italic text-[#A47E2A]">refuse the ordinary</span>.</h2>
            <p className="text-[#756E64] font-light mt-6">Every mission is owned by a single senior operations lead. Every note is on record. Every permit is filed hours before it's required. Nothing is left to chance — and nothing shows up on your phone unless it needs your attention.</p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              <div><div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Response SLA</div><div className="as-serif text-2xl mt-1">≤ 6 min</div></div>
              <div><div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Ops Owners</div><div className="as-serif text-2xl mt-1">1 per trip</div></div>
              <div><div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Concierge Categories</div><div className="as-serif text-2xl mt-1">18</div></div>
              <div><div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Vetted Suppliers</div><div className="as-serif text-2xl mt-1">340+</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1474302770737-173ee21bab63?auto=format&fit=crop&w=1800&q=80" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D24] via-[#1A1D24]/80 to-[#1A1D24]/50"/>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center text-white">
          <div className="as-overline mb-6 !text-white/60">03 — Begin</div>
          <h2 className="as-serif text-5xl md:text-7xl font-light text-white">Move like it's <span className="italic text-[#E5C158]">weightless</span>.</h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/charter/inquiry" className="as-btn-gold px-7 py-4 rounded-sm as-mono text-xs uppercase tracking-[0.2em]" data-testid="cta-inquiry">Request a Quote</Link>
            <Link to="/register" className="px-7 py-4 rounded-sm as-mono text-xs uppercase tracking-[0.2em] border border-white/40 text-white hover:border-white hover:bg-white/10 transition-colors" data-testid="cta-register">Open a Client Account</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5DFD2] py-10 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-6 text-[#756E64] as-mono text-[10px] uppercase tracking-widest">
          <div>© 2026 AeroSphere Global</div>
          <div>Discretion · Precision · Sovereignty</div>
        </div>
      </footer>
    </div>
  );
}
